import React, {Component} from 'react';
import styled from 'styled-components';
import TransportBar from './TransportBar';
import {STATUS_COMPLETED} from 'utils/asset/getUserAssetStatus';

export const ProgressContext = React.createContext({
	loaded: null,
	played: null,
	elapsedTime: null,
	duration: null,
});

const Wrapper = styled.div`
	position: relative;
	display: flex;
	flex-direction: column;
	justify-content: center;
	width: 100%;
	height: 100%;
`;

const Frame = styled.div`
	position: relative;
	width: 100%;
`;

class AudioPlayer extends Component {
	constructor(props) {
		super(props);
		this.state = {
			progress: {},
			playing: false,
			duration: '',
			isSeeking: false,
			elapsedTime: undefined,
			completed: this.props.userContentStatus === STATUS_COMPLETED,
		};
		this.wrapperRef = c => (this.container = c);
		this.playerRef = c => (this.player = c);
		this.progress = this.progress.bind(this);
		this.getFractionLoaded = this.getFractionLoaded.bind(this);
		this.getFractionPlayed = this.getFractionPlayed.bind(this);
		this.getDuration = this.getDuration.bind(this);
		this._seekTo = this._seekTo.bind(this);
		this._onprogress = this._onprogress.bind(this);
		this._isAlmostFinished = this._isAlmostFinished.bind(this);
		this.isPlaying = this.isPlaying.bind(this);
		this._play = this._play.bind(this);
		this._handleLoad = this._handleLoad.bind(this);
		this._handleCanPlay = this._handleCanPlay.bind(this);
		this._handlePlay = this._handlePlay.bind(this);
		this._handlePaused = this._handlePaused.bind(this);
		this._handleEnded = this._handleEnded.bind(this);
		this._handleError = this._handleError.bind(this);
	}

	componentWillUnmount() {
		// Kill the progress tick
		clearTimeout(this.progressTimeout);
	}

	componentDidMount() {
		// Start the progress tick
		this.progress();
	}

	progress() {
		if (this.props.url && this.player) {
			const progress = {};

			progress.loaded = this.getFractionLoaded();
			progress.played = this.getFractionPlayed();
			progress.elapsed = this.player.currentTime;

			if (progress.loaded || progress.played) {
				// Fire events
				this.props.onProgress(progress);

				// Handle default events
				this._onprogress(progress);

				if (progress.played === 1) {
					this.isPlaying(false);
				}

				if (
					this.props.autoCompleteEnabled &&
					!this.state.completed &&
					this._isAlmostFinished()
				) {
					this.props.onComplete();
					this.setState({completed: true});
				}
			}
		}

		// Call this again
		this.progressTimeout = setTimeout(
			this.progress,
			this.props.progressFrequency
		);
	}

	getFractionLoaded() {
		if (!this.isReady || this.player.buffered.length === 0) return null;
		return (
			this.player.buffered.end(this.player.buffered.length - 1) /
			this.getDuration()
		);
	}

	getFractionPlayed() {
		if (!this.isReady) return null;
		return this.player.currentTime / this.getDuration();
	}

	getDuration() {
		return this.isReady ? this.player.duration : null;
	}

	_seekTo(e) {
		// Calculate the position along the seek_bar
		const played = e.nativeEvent.offsetX / e.currentTarget.offsetWidth;

		// ... and trigger state change
		this.setState({
			progress: Object.assign(this.state.progress, {played}),
			isSeeking: true,
		});

		setTimeout(() => {
			this.setState({isSeeking: false});
		}, 50);

		// What does this translate to
		this.player.currentTime = this.getDuration() * played;
	}

	_onprogress(progress) {
		this.setState({progress, elapsedTime: progress.elapsed});
	}

	_isAlmostFinished() {
		const duration = this.getDuration();

		if (!duration) {
			return false;
		}

		return (this.state.elapsedTime * 100) / duration >= 95; // we played 95% or more
	}

	isPlaying(val) {
		this.setState({playing: val});
	}

	_play() {
		const m = this.player;

		if (!this.state.playing) {
			m.play();
			this.isPlaying(true);
		} else {
			m.pause();
			this.isPlaying(false);
		}
	}

	_handleLoad(e) {
		this.props.onLoad(e);
	}

	_handleCanPlay() {
		// the media element has been appended to the DOM.
		this.isReady = true;

		// Set duration
		const duration = this.getDuration();
		this.setState({duration});
	}

	_handlePlay() {
		this.isPlaying(true);
	}

	_handlePaused() {
		this.isPlaying(false);
	}

	_handleEnded() {
		this.isPlaying(false);
	}

	_handleError(e) {
		let code;
		let message;

		const err = e.target.error;
		switch (err.code) {
			case err.MEDIA_ERR_ABORTED:
				code = 'MEDIA_ERR_ABORTED';
				message = 'You aborted the media playback.';
				break;
			case err.MEDIA_ERR_NETWORK:
				code = 'MEDIA_ERR_NETWORK';
				message =
					'A network error caused the media download to fail part-way.';
				break;
			case err.MEDIA_ERR_DECODE:
				code = 'MEDIA_ERR_DECODE';
				message =
					'The media playback was aborted due to a corruption problem or because the video used features your browser did not support.';
				break;
			case err.MEDIA_ERR_SRC_NOT_SUPPORTED:
				code = 'MEDIA_ERR_SRC_NOT_SUPPORTED';
				message =
					'The media could not be loaded, either because the server or network failed or because the format is not supported.';
				break;
			default:
				code = 'unknown';
				message = 'An unknown error occurred.';
				break;
		}

		this.props.onError({
			code,
			message,
		});
	}

	render() {
		const {url, className} = this.props;

		const {playing, progress, elapsedTime, duration, isSeeking} =
			this.state;

		return (
			<Wrapper ref={this.wrapperRef} className={className}>
				<Frame onClick={this._play}>
					{url && ( // eslint-disable-next-line jsx-a11y/media-has-caption
						<audio
							ref={this.playerRef}
							preload="metadata"
							src={url}
							onLoadedData={this._handleLoad}
							onError={this._handleError}
							onCanPlay={this._handleCanPlay}
							onPlay={this._handlePlay}
							onPause={this._handlePaused}
							onEnded={this._handleEnded}
							id="mediaplayer"
						/>
					)}
				</Frame>
				<ProgressContext.Provider
					value={{
						duration,
						elapsedTime,
						loaded: progress.loaded,
						played: progress.played,
					}}
				>
					<TransportBar
						isPlaying={playing}
						isSeeking={isSeeking}
						onPlay={this._play}
						onSeek={this._seekTo}
					/>
				</ProgressContext.Provider>
			</Wrapper>
		);
	}
}

AudioPlayer.defaultProps = {
	playing: false,
	url: '',
	volume: 0.8,
	progressFrequency: 1000,
	autoCompleteEnabled: false,
	onPlay() {},
	onPause() {},
	onBuffer() {},
	onEnded() {},
	onDuration() {},
	onProgress() {},
	onLoad() {},
	onError() {},
	onComplete() {},
};

export default AudioPlayer;
